import GiftCard from '../GiftCard';
import { useQuery } from '@tanstack/react-query';
import { getGiftCards, TGetGiftCardsRes } from '@/services/giftCards';
import RefundGiftDrawer from '../RefundGiftDrawer';
import { useState } from 'react';
import { useRefundDetails } from '../../hooks/useRefundDetails';
import { getDigitalPayments } from '@/services/digitalPayments';
import { useTranslation } from 'react-i18next';

type TDigitalPaymentType = 'giftcards' | 'donations' | 'digitalpayments';

const GiftCardRefund = ({ type }: { type: TDigitalPaymentType }) => {
  const { t } = useTranslation();
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const setSelectedOption = (idx: number) => setSelectedIdx(idx);
  const { data: refundData } = useRefundDetails();

  const { data, isPending } = useQuery({
    queryKey: ['giftCards', type],
    queryFn: () => {
      if (['giftcards', 'donations'].includes(type)) {
        return getGiftCards({
          type,
        });
      }
      return getDigitalPayments();
    },
  });

  if (isPending) return 'Loading...';

  const brands: TGetGiftCardsRes['catalog'][0] | undefined =
    selectedIdx !== null ? data?.catalog[selectedIdx] : undefined;

  const onNextOption = () => {
    if (!data) return;

    if (selectedIdx === data?.catalog?.length - 1) {
      setSelectedIdx(0);
      return;
    }

    setSelectedIdx((prev) => (prev ?? 0) + 1);
  };

  const onPrevOption = () => {
    if (!data) return;

    if (selectedIdx === 0) {
      setSelectedIdx(data.catalog.length - 1);
      return;
    }

    setSelectedIdx((prev) => (prev ?? 0) - 1);
  };

  const selectOneByType = {
    giftcards: 'Gift Card Payment',
    donations: 'Charity to Donate',
    digitalpayments: 'Digital Payment',
  };

  return (
    <>
      <div className='mb-6'>
        <div className='my-4 flex items-center justify-between'>
          <h6 className='font-medium text-foreground'>
            {t('Select One :', { type: selectOneByType[type] })}
          </h6>
        </div>
        <div className='flex flex-wrap gap-6'>
          {data?.catalog?.map((option, idx) => (
            <GiftCard
              key={idx}
              {...option}
              idx={idx}
              type={type}
              setSelectedOption={setSelectedOption}
              setIsOpen={setIsOpen}
              refundAmount={Number(refundData?.refundData.refund_amount ?? 0)}
            />
          ))}
        </div>
      </div>
      {brands && (
        <RefundGiftDrawer
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          {...brands.brands[0]}
          type={type}
          onPrevOption={onPrevOption}
          onNextOption={onNextOption}
        />
      )}
    </>
  );
};

export default GiftCardRefund;
